import { ReactNode } from "react";
import { classNames } from "primereact/utils";
import { Icon, IconType } from "../../../icons/Icon";
import { DefaultButton } from "../../button/defaultButton/DefaultButton";
import { RenderWhen } from "../../../infrastructure/components/RenderWhen";
import styles from "./EmptyStateCard.module.scss";

/** Same lavender the workout cards use for their glass icons. */
const ICON_COLOR = "rgba(180, 178, 235, 0.95)";

interface IEmptyStateCardProps {
  className?: string;
  /** Icon shown in the orb next to the title. */
  icon: IconType;
  title: string;
  description?: ReactNode;
  /** Short "what you get" rows rendered as a checked list under the description. */
  highlights?: string[];
  /** Call-to-action label. The button renders only when onAction is passed too. */
  actionLabel?: string;
  /** Hint shown on the call-to-action, e.g. why it is unavailable right now. */
  actionTooltip?: string;
  onAction?: () => void;
}

/**
 * The big glassmorphism empty state shown when a whole page (or tab) has no data
 * yet: an icon orb next to the title, the explanation underneath, an optional
 * "what you get" list and an optional call-to-action. Shared by the portal pages
 * so every first-time screen looks the same - the smaller inline EmptyState stays
 * for lists inside a panel.
 */
export const EmptyStateCard = ({
  className,
  icon,
  title,
  description,
  highlights,
  actionLabel,
  actionTooltip,
  onAction,
}: IEmptyStateCardProps) => {
  return (
    <div className={classNames(styles.root, className)}>
      <div className={styles.root_header}>
        <div className={styles.root_header_orb}>
          <Icon type={icon} width="26px" height="26px" color={ICON_COLOR} />
        </div>

        <h2 className={styles.root_header_title}>{title}</h2>
      </div>

      <RenderWhen condition={!!description}>
        <p className={styles.root_description}>{description}</p>
      </RenderWhen>

      <RenderWhen condition={!!highlights?.length}>
        <ul className={styles.root_highlights}>
          {highlights?.map((highlight) => (
            <li className={styles.root_highlights_item} key={highlight}>
              {highlight}
            </li>
          ))}
        </ul>
      </RenderWhen>

      <RenderWhen condition={!!actionLabel && !!onAction}>
        <DefaultButton
          className={styles.root_action}
          label={actionLabel ?? ""}
          tooltip={actionTooltip}
          onClick={onAction}
          width="100%"
        />
      </RenderWhen>
    </div>
  );
};
